import {Controller, useForm} from "react-hook-form";
import "../styles/InitialFormPage.css";
import {FormValues} from "domain/types";
import {useEffect, useState} from "react";
import {RUTAS} from "constants/app";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useToken} from "application/hooks/useToken";
import {useInitialFormFields} from "application/hooks/useInitialFormFields";
import {SelectComponent} from "ui/components/SelectComponent";
import {useClientMetadata} from "application/hooks/useClientMetadata";
import {mapDemographicsToFormValues, mapFormValuesToDemographics} from "application/utils/demographicsMapper";
import {useTheme} from "../providers/ThemeProvider";

export const InitialFormPage = () => {
    useToken();
    const {departments} = useClientMetadata();
    const [occupationOptions, setOccupationOptions] = useState<{ value: string; label: string }[]>([]);
    const {theme} = useTheme();

    const fields = useInitialFormFields({departments});

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = localStorage.getItem("user-token");
    const urlToken = searchParams.get("token");

    const EMPTY_FORM_VALUES: FormValues = {
        sexo: "",
        edad: "",
        estadoCivil: "",
        ocupacionProfesionPuesto: "",
        departamentoSeccionArea: "",
        nivelEstudios: "",
        estadoNivelEstudios: "",
        tipoPuesto: "",
        tipoContratacion: "",
        tipoPersonal: "",
        tipoJornada: "",
        rotacionTurno: undefined,
        antiguedad: "",
        experienciaLaboral: "",
    };

    const mapStr = localStorage.getItem("NOM035_tokenMap");
    const demographicsRaw = token && mapStr ? JSON.parse(mapStr)?.[token]?.demographics : undefined;

    const defaultData: FormValues = demographicsRaw
        ? mapDemographicsToFormValues(demographicsRaw)
        : EMPTY_FORM_VALUES;

    const {
        watch,
        handleSubmit,
        control,
        reset,
        trigger,
        setValue,
    } = useForm<FormValues>({
        defaultValues: defaultData,
        mode: "onChange",
        reValidateMode: "onChange",
    });

    const selectedDepartment = watch("departamentoSeccionArea");

    useEffect(() => {
        const department = departments.find(department => department.name === selectedDepartment);
        const newOptions = department?.occupations.map(occupation => ({
            value: occupation.name,
            label: occupation.name
        })) ?? [];
        setOccupationOptions(newOptions);
    }, [selectedDepartment, departments]);


    const TIME_RANGES_ORDER = [
        "Menos de 6 meses",
        "Entre 6 meses y 1 año",
        "Entre 1 a 4 años",
        "Entre 5 a 9 años",
        "Entre 10 a 14 años",
        "Entre 15 a 19 años",
        "Entre 20 a 24 años",
        "25 años o más",
    ] as const;

    type RangeLabel = typeof TIME_RANGES_ORDER[number];

    const isRangeLabel = (v: string): v is RangeLabel =>
        (TIME_RANGES_ORDER as readonly string[]).includes(v);

    const rankRange = (v?: string) => {
        if (!v) return -1;
        return isRangeLabel(v) ? TIME_RANGES_ORDER.indexOf(v) : -1;
    };

    useEffect(() => {
        if (demographicsRaw) {
            reset(mapDemographicsToFormValues(demographicsRaw));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        window.history.replaceState(null, '', window.location.href);

        if (!urlToken) {
            navigate(RUTAS.ACCESO);
            return;
        }

        if (urlToken !== token) {
            navigate(`${RUTAS.ACCESO}?token=${urlToken}`);
        }
    }, [urlToken, token, navigate]);

    const isDemographicsComplete = (v: FormValues) => (
        !!v.sexo &&
        !!v.edad &&
        !!v.ocupacionProfesionPuesto &&
        !!v.departamentoSeccionArea &&
        !!v.nivelEstudios &&
        !!v.estadoNivelEstudios &&
        !!v.tipoPuesto &&
        !!v.tipoContratacion &&
        !!v.tipoPersonal &&
        !!v.tipoJornada &&
        v.rotacionTurno !== undefined &&
        !!v.antiguedad &&
        !!v.experienciaLaboral
    );

    useEffect(() => {
        const subscription = watch((values) => {
            const payload: FormValues = {
                sexo: values.sexo || "",
                edad: values.edad || "",
                estadoCivil: values.estadoCivil || "",
                ocupacionProfesionPuesto: values.ocupacionProfesionPuesto || "",
                departamentoSeccionArea: values.departamentoSeccionArea || "",
                nivelEstudios: values.nivelEstudios || "",
                estadoNivelEstudios: values.estadoNivelEstudios || "",
                tipoPuesto: values.tipoPuesto || "",
                tipoContratacion: values.tipoContratacion || "",
                tipoPersonal: values.tipoPersonal || "",
                tipoJornada: values.tipoJornada || "",
                rotacionTurno: values.rotacionTurno,
                antiguedad: values.antiguedad || "",
                experienciaLaboral: values.experienciaLaboral || "",
            };

            const token = localStorage.getItem("user-token");
            if (!token) return;

            const mapStr = localStorage.getItem("NOM035_tokenMap");
            const map = mapStr ? JSON.parse(mapStr) : {};

            if (!map[token]) {
                map[token] = {
                    answers: {},
                    scores: {},
                };
            }

            if (isDemographicsComplete(payload)) {
                map[token].demographics = mapFormValuesToDemographics(payload);
                localStorage.setItem("NOM035_tokenMap", JSON.stringify(map));
            }
        });

        return () => subscription.unsubscribe();
    }, [watch]);

    const onSubmit = () => {
        navigate(`${RUTAS.GUIA_I}?token=${urlToken}`);
    };

    const onError = (errors: any) => {
        console.log("Errores de validación:", errors);
        const firstErrorKey = Object.keys(errors)[0];
        if (!firstErrorKey) return;
        const errorElement = document.querySelector(`[name="${firstErrorKey}"]`)
            ?? document.getElementById(firstErrorKey)
            ?? document.querySelector(`[inputid="${firstErrorKey}"]`);
        if (errorElement) {
            errorElement.scrollIntoView({behavior: "smooth", block: "center"});
        }
    };

    const logoSrc = theme?.assets.logoMovilInitialForm ?? theme?.assets.logoMovil;

    return (
        <div className="form-wrapper">
            {logoSrc && (
                <div className="mobile-logo-header">
                    <img src={logoSrc} alt="Mobile Logo"/>
                </div>
            )}
            <div className="form-card">
                <h1 className="title-form">Datos del trabajador</h1>
                <hr/>
                <p className="subtitle-form">
                    1. Antes de iniciar con el cuestionario, completa el siguiente formulario.
                </p>
                <p className="text-form">
                    <i>El contenido de esta guía es un complemento para la mejor comprensión de la presente Norma.</i>
                </p>
                <form onSubmit={handleSubmit(onSubmit, onError)} className="form-grid">
                    {fields.map((field) => {
                        const isOccupationField = field.name === "ocupacionProfesionPuesto";
                        const isDepartmentField = field.name === "departamentoSeccionArea";
                        const options = isOccupationField ? occupationOptions : field.options;

                        return (
                            <Controller
                                key={field.name}
                                name={field.name}
                                control={control}
                                rules={
                                    field.required
                                        ? field.name === "rotacionTurno"
                                            ? {validate: (v) => v !== undefined || "*Favor de completar"}
                                            : field.name === "antiguedad"
                                                ? {
                                                    required: "*Favor de completar",
                                                    validate: (v, formValues) => {
                                                        if (!v || typeof v !== "string") return "*Favor de completar";
                                                        const exp = (formValues as FormValues)?.experienciaLaboral;
                                                        if (!exp) return true;
                                                        const ok = rankRange(v) <= rankRange(exp);
                                                        return ok || "El tiempo en puesto actual no puede ser mayor que tu experiencia laboral.";
                                                    }
                                                }
                                                : field.name === "experienciaLaboral"
                                                    ? {
                                                        required: "*Favor de completar",
                                                        validate: (v, formValues) => {
                                                            if (!v || typeof v !== "string") return "*Favor de completar";
                                                            const ant = (formValues as FormValues)?.antiguedad;
                                                            if (!ant) return true;
                                                            const ok = rankRange(ant) <= rankRange(v);
                                                            return ok || "El tiempo en puesto actual no puede ser mayor que tu experiencia laboral.";
                                                        }
                                                    }
                                                    : {required: "*Favor de completar"}
                                        : {}
                                }
                                render={({field: controllerField, fieldState}) => {
                                    const isBooleanField = field.name === "rotacionTurno";
                                    const raw = controllerField.value;
                                    const valueForSelect: string = isBooleanField
                                        ? (raw === true ? "true" : raw === false ? "false" : "")
                                        : (typeof raw === "string" ? raw : "");

                                    const handleChangeForSelect = (v: string) => {
                                        if (isBooleanField) {
                                            controllerField.onChange(v === "" ? undefined : v === "true");
                                        } else {
                                            controllerField.onChange(v);
                                        }

                                        if (isDepartmentField) {
                                            setValue("ocupacionProfesionPuesto", "");
                                        }

                                        if (field.name === "antiguedad") {
                                            trigger("experienciaLaboral");
                                        } else if (field.name === "experienciaLaboral") {
                                            trigger("antiguedad");
                                        }
                                    };

                                    return (
                                        <SelectComponent
                                            name={field.name}
                                            label={field.label}
                                            options={options}
                                            value={valueForSelect}
                                            onChange={handleChangeForSelect}
                                            error={fieldState.error?.message}
                                            required={field.required}
                                            disabled={isOccupationField ? !selectedDepartment || options.length === 0 : (Array.isArray(field.options) && field.options.length === 0)}
                                            emptyText={isOccupationField ? "Seleccione un departamento" : "No hay registros"}
                                        />
                                    );
                                }}
                            />
                        );
                    })}

                    <div className="button-next desktop-btn">
                        <button type="submit" className="btn">
                            Siguiente
                        </button>
                    </div>
                </form>
            </div>
            <div className="button-next full-width-row mobile-btn">
                <button type="submit" form="initial-form" className="btn">
                    Guardar
                </button>
            </div>
        </div>
    );
};
